// FRACAS List Page - View and manage all failure incidents
// ============================================================

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFracas } from '../contexts/FracasContext';
import { getSeverityColor, getStatusColor } from '../utils/fracasHelpers';
import {
    AlertTriangle,
    Plus,
    Search,
    Calendar,
    User,
    TrendingUp,
    Clock,
    AlertCircle,
} from 'lucide-react';

export const FracasList: React.FC = () => {
    const { incidents, getIncidentsByStatus, getIncidentsBySeverity } = useFracas();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('All');
    const [severityFilter, setSeverityFilter] = useState<string>('All');

    // Filter incidents
    const filteredIncidents = incidents.filter(incident => {
        const matchesSearch =
            incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            incident.fracasNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
            incident.productName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesStatus = statusFilter === 'All' || incident.status === statusFilter;
        const matchesSeverity = severityFilter === 'All' || incident.severity === severityFilter;

        return matchesSearch && matchesStatus && matchesSeverity;
    });

    // Summary stats
    const openIncidents = getIncidentsByStatus('Open').length;
    const criticalIncidents = getIncidentsBySeverity('Critical').length;
    const inProgressIncidents = incidents.filter(
        i => i.status === 'Investigating' || i.status === 'Analysis'
    ).length;

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <AlertTriangle className="w-8 h-8 text-red-600" />
                            FRACAS Incidents
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Failure Reporting, Analysis, and Corrective Action System
                        </p>
                    </div>
                    <Link
                        to="/fracas/create"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Plus className="w-5 h-5" />
                        New Incident
                    </Link>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Total Incidents</p>
                                <p className="text-2xl font-bold text-gray-900">{incidents.length}</p>
                            </div>
                            <AlertTriangle className="w-8 h-8 text-gray-400" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Open</p>
                                <p className="text-2xl font-bold text-blue-600">{openIncidents}</p>
                            </div>
                            <Clock className="w-8 h-8 text-blue-400" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">Critical</p>
                                <p className="text-2xl font-bold text-red-600">{criticalIncidents}</p>
                            </div>
                            <AlertCircle className="w-8 h-8 text-red-400" />
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600">In Progress</p>
                                <p className="text-2xl font-bold text-purple-600">{inProgressIncidents}</p>
                            </div>
                            <TrendingUp className="w-8 h-8 text-purple-400" />
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex-1 min-w-[300px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search incidents..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="All">All Status</option>
                        <option value="Open">Open</option>
                        <option value="Investigating">Investigating</option>
                        <option value="Analysis">Analysis</option>
                        <option value="Action">Action</option>
                        <option value="Verification">Verification</option>
                        <option value="Closed">Closed</option>
                    </select>
                    <select
                        value={severityFilter}
                        onChange={e => setSeverityFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="All">All Severity</option>
                        <option value="Critical">Critical</option>
                        <option value="Major">Major</option>
                        <option value="Minor">Minor</option>
                        <option value="Cosmetic">Cosmetic</option>
                    </select>
                </div>
            </div>

            {/* Incidents Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Incident
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Product
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Severity
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Reported
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredIncidents.map(incident => (
                                <tr key={incident.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div>
                                            <Link
                                                to={`/fracas/${incident.id}`}
                                                className="text-blue-600 hover:text-blue-800 font-medium"
                                            >
                                                {incident.fracasNumber}
                                            </Link>
                                            <p className="text-sm text-gray-600 mt-1">{incident.title}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">{incident.productName}</div>
                                        {incident.partNumber && (
                                            <div className="text-xs text-gray-500">{incident.partNumber}</div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(
                                                incident.severity
                                            )}`}
                                        >
                                            {incident.severity}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span
                                            className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                                incident.status
                                            )}`}
                                        >
                                            {incident.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <Calendar className="w-4 h-4" />
                                            {new Date(incident.reportedAt).toLocaleDateString()}
                                        </div>
                                        {incident.assignedToName && (
                                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                                <User className="w-3 h-3" />
                                                {incident.assignedToName}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <Link
                                            to={`/fracas/${incident.id}`}
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                        >
                                            View Details →
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {filteredIncidents.length === 0 && (
                    <div className="text-center py-12">
                        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">No incidents found matching your filters.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FracasList;
